import { useCallback, useEffect, useMemo, useState } from 'react';

import { POS_TYPE, ROLE_PERMISSIONS, STATUS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { getFullMachineName } from '../../../common/utils/machine';
import { getPickListStatusLabel } from '../../../common/utils/pickList/getPickListStatusLabel';
import {
	MY_DAY_STEP_STATUS,
	buildAccessPreparationRows,
	buildEndOfDaySteps,
	buildPerStopSteps,
	buildStartOfDaySteps,
	docketRequiresMoneyBagReturn,
	getNextAction,
	isServiceRunStarted,
} from '../../../common/utils/myDay/driverDaySteps';
import { useOfflineQueue } from '../../offline';
import useDriverMyDayData from '../myDay/useDriverMyDayData';
import useMyDayStepSkips from '../myDay/useMyDayStepSkips';
import {
	getMyDayTrainingRouteParams,
	isMyDayTraining,
	lockMyDayTrainingEndSteps,
	lockMyDayTrainingPersonalSteps,
} from '../myDay/myDayTraining';

const COUNTABLE_STEP_STATUSES = [MY_DAY_STEP_STATUS.DONE, MY_DAY_STEP_STATUS.TODO, MY_DAY_STEP_STATUS.BLOCKED];

const getDocketName = docket => getFullMachineName(docket?.machine, docket?.machine?.place, docket?.machine?.location);

const hasPendingSync = (queue, configKey, docketId, { ignoreFailed = false } = {}) => Boolean(docketId) && (queue || []).some(entry => (
	entry.configKey === configKey &&
	entry.meta?.serviceDocketId === docketId &&
	(!ignoreFailed || !entry.failureCount)
));

/**
 * The driver's day: data, the selected run and stop, and every step list built
 * by `src/common/utils/myDay/driverDaySteps.js`. Extracted from the classic My
 * day board without behaviour change; the board (DriverMyDayView) and the
 * redesigned driver screens (through DriverDayProvider/useDriverDay) both use it.
 * Navigation and mutations live in useDriverDayActions.
 *
 * Params: { trainingContext, isFocused, skip } (isFocused/skip see useDriverMyDayData).
 */
const useDriverDayModel = ({ trainingContext = null, isFocused, skip = false } = {}) => {
	const { tenant, tenantSettings, hasPermission } = useUserContext();
	const { queue, isOnline } = useOfflineQueue();
	const isTraining = isMyDayTraining(trainingContext);
	const trainingOffline = isTraining && !isOnline;
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);
	const trainingRouteParams = useMemo(() => getMyDayTrainingRouteParams(trainingContext), [trainingContext]);

	const data = useDriverMyDayData(trainingContext, { isFocused, skip });
	const {
		activeRun: serviceRun,
		clockStatus,
		clockOutReminder,
		allAssignedRunsComplete,
		checklistSubmissions,
		pendingChecklistPurposes,
		isDocketDone,
		isDocketHandled,
		todayDateKey,
	} = data;

	const [selectedDocketId, setSelectedDocketId] = useState(null);

	const sortedDockets = useMemo(() => {
		if (!serviceRun?.serviceDockets?.length) {
			return [];
		}

		// My Day follows the persisted route positions. Service-time sorting can
		// otherwise undo a driver's custom order when the run is loaded again.
		return [...serviceRun.serviceDockets].sort((docketA, docketB) => docketA.order - docketB.order);
	}, [serviceRun?.serviceDockets]);
	const hasEligiblePickListItems = useMemo(() => sortedDockets.some(docket => (
		docket.pickList?.id && Number(docket.totalPickItemsQuantity) > 0
	)), [sortedDockets]);

	// "What's next" passes over stops the driver marked "Couldn't complete".
	const nextIncompleteDocketIndex = useMemo(() => sortedDockets.findIndex(docket => !isDocketHandled(docket)), [isDocketHandled, sortedDockets]);
	const nextIncompleteDocket = nextIncompleteDocketIndex > -1 ? sortedDockets[nextIncompleteDocketIndex] : null;

	useEffect(() => {
		if (!sortedDockets.length) {
			if (selectedDocketId) {
				setSelectedDocketId(null);
			}
			return;
		}

		// A manual selection only sticks while that stop is still incomplete;
		// once it completes the board advances to the next incomplete stop.
		const selectedDocket = selectedDocketId && sortedDockets.find(docket => docket.id === selectedDocketId);
		if (selectedDocket && !isDocketHandled(selectedDocket)) {
			return;
		}

		if (nextIncompleteDocket) {
			if (selectedDocketId !== nextIncompleteDocket.id) {
				setSelectedDocketId(nextIncompleteDocket.id);
			}
			return;
		}

		if (selectedDocketId) {
			setSelectedDocketId(null);
		}
	}, [isDocketHandled, nextIncompleteDocket, selectedDocketId, sortedDockets]);

	const activeDocket = useMemo(() => {
		if (!sortedDockets.length) {
			return null;
		}

		if (selectedDocketId) {
			const selectedDocket = sortedDockets.find(docket => docket.id === selectedDocketId);
			// Never treat a completed stop as the current one, regardless of
			// selection-effect timing.
			if (selectedDocket && !isDocketHandled(selectedDocket)) {
				return selectedDocket;
			}
		}

		return nextIncompleteDocket || null;
	}, [isDocketHandled, nextIncompleteDocket, selectedDocketId, sortedDockets]);

	const activeDocketIndex = useMemo(() => {
		if (!activeDocket?.id) {
			return -1;
		}

		return sortedDockets.findIndex(docket => docket.id === activeDocket.id);
	}, [activeDocket?.id, sortedDockets]);
	const upcomingDocket = useMemo(() => {
		if (activeDocketIndex < 0) {
			return null;
		}

		return [
			...sortedDockets.slice(activeDocketIndex + 1),
			...sortedDockets.slice(0, activeDocketIndex),
		].find(docket => !isDocketHandled(docket)) || null;
	}, [activeDocketIndex, isDocketHandled, sortedDockets]);

	// The Run page's "Now" stop follows the route, never the stop last opened:
	// a stop already being serviced (machine processing a restock), else the
	// first unfinished stop in order.
	const nowDocket = useMemo(() => (
		sortedDockets.find(docket => docket.startedProcessing && !isDocketHandled(docket)) || nextIncompleteDocket
	), [isDocketHandled, nextIncompleteDocket, sortedDockets]);

	const routeMapPoints = useMemo(() => {
		const points = [];

		if (serviceRun?.startLocation) {
			points.push({
				id: `start_${serviceRun.startLocation.id || 'unknown'}`,
				kind: 'start',
				label: 'Start',
				name: serviceRun.startLocation.name || 'Start location',
				location: serviceRun.startLocation,
			});
		}

		sortedDockets.forEach((docket, index) => {
			const machineName = getDocketName(docket);
			const isCurrent = docket.id === nowDocket?.id;
			const isComplete = isDocketDone(docket);
			const stopLabel = machineName || `Stop ${index + 1}`;

			points.push({
				id: `stop_${docket.id}`,
				kind: 'stop',
				label: stopLabel,
				name: machineName,
				location: docket.machine?.location,
				status: isComplete ? 'complete' : isCurrent ? 'current' : 'pending',
			});
		});

		if (serviceRun?.endLocation) {
			points.push({
				id: `end_${serviceRun.endLocation.id || 'unknown'}`,
				kind: 'end',
				label: 'End',
				name: serviceRun.endLocation.name || 'End location',
				location: serviceRun.endLocation,
			});
		}

		return points;
	}, [
		isDocketDone,
		nowDocket?.id,
		serviceRun?.endLocation,
		serviceRun?.startLocation,
		sortedDockets,
	]);

	const {
		skippedByDocketId: skippedPickStepByDocketId,
		skipDocket: skipPickStepForDocket,
		clearDocket: clearPickStepSkip,
		isLoading: isLoadingPickSkips,
	} = useMyDayStepSkips({
		prefix: 'my_day_skipped_pick_step',
		serviceRunId: serviceRun?.id,
		planned: serviceRun?.planned,
		timezone: tenant?.timezone,
		disabled: isTraining,
	});
	const {
		skippedByDocketId: skippedRestockStepByDocketId,
		skipDocket: skipRestockStepForDocket,
		clearDocket: clearRestockStepSkip,
		isLoading: isLoadingRestockSkips,
	} = useMyDayStepSkips({
		prefix: 'my_day_skipped_restock_step',
		serviceRunId: serviceRun?.id,
		planned: serviceRun?.planned,
		timezone: tenant?.timezone,
		disabled: isTraining,
	});

	// A step completing for real clears its local skip.
	useEffect(() => {
		if (activeDocket?.id && activeDocket.inventoryPicked) {
			clearPickStepSkip(activeDocket.id);
		}
	}, [activeDocket?.id, activeDocket?.inventoryPicked, clearPickStepSkip]);
	useEffect(() => {
		if (activeDocket?.id && activeDocket.isRestocked) {
			clearRestockStepSkip(activeDocket.id);
		}
	}, [activeDocket?.id, activeDocket?.isRestocked, clearRestockStepSkip]);

	const getDocketNavigationParams = useCallback((docket) => {
		const isMarket = docket.machine?.type === POS_TYPE.MARKET;
		return {
			docketId: docket.id,
			machineId: docket.machine?.id,
			machineName: getDocketName(docket),
			machineType: docket.machine?.type,
			isMarket,
			...trainingRouteParams,
		};
	}, [trainingRouteParams]);

	const reorderableDockets = useMemo(() => (
		sortedDockets.filter(docket => (
			docket.status !== STATUS.COMPLETE && !docket.startedProcessing
		))
	), [sortedDockets]);
	const reorderableStops = useMemo(() => (
		reorderableDockets.map(docket => ({
			id: docket.id,
			name: getDocketName(docket) || 'Unnamed stop',
		}))
	), [reorderableDockets]);

	// Per-stop steps for any stop of the active run, with the same device-local
	// skips and pending offline syncs the board applies to the current stop.
	const buildStopSteps = useCallback(docket => buildPerStopSteps(docket, {
		skippedPickStep: Boolean(docket?.id && skippedPickStepByDocketId[docket.id]),
		skippedRestockStep: Boolean(docket?.id && skippedRestockStepByDocketId[docket.id]),
		hasPendingConfirmStockSync: hasPendingSync(scopedQueue, 'confirmStockLevels', docket?.id, { ignoreFailed: true }),
		hasPendingConfirmValuesSync: hasPendingSync(scopedQueue, 'confirmMachineValues', docket?.id),
		isDeviceOffline: !isOnline,
	}), [isOnline, scopedQueue, skippedPickStepByDocketId, skippedRestockStepByDocketId]);

	const startOfDaySteps = useMemo(() => lockMyDayTrainingPersonalSteps(buildStartOfDaySteps({
		run: serviceRun,
		clockStatus,
		tenantSettings,
		checklistSubmissions,
		pendingChecklistPurposes,
		todayDateKey,
		timezone: tenant?.timezone,
		isDocketComplete: isDocketDone,
	}), trainingContext), [
		checklistSubmissions,
		clockStatus,
		pendingChecklistPurposes,
		isDocketDone,
		serviceRun,
		tenant?.timezone,
		tenantSettings,
		todayDateKey,
		trainingContext,
	]);

	const perStopSteps = useMemo(() => buildStopSteps(activeDocket), [activeDocket, buildStopSteps]);

	const endOfDaySteps = useMemo(() => lockMyDayTrainingEndSteps({
		steps: buildEndOfDaySteps({
			run: serviceRun,
			sortedDockets,
			checklistSubmissions,
			pendingChecklistPurposes,
			tenantSettings,
			clock: clockStatus,
			clockOutReminder,
			allAssignedRunsComplete,
			timezone: tenant?.timezone,
		}),
		trainingContext,
		allAssignedRunsComplete,
	}), [
		allAssignedRunsComplete,
		checklistSubmissions,
		clockStatus,
		clockOutReminder,
		pendingChecklistPurposes,
		serviceRun,
		sortedDockets,
		tenant?.timezone,
		tenantSettings,
		trainingContext,
	]);

	const runStarted = useMemo(() => isServiceRunStarted(serviceRun), [serviceRun]);

	const nextAction = useMemo(() => getNextAction({
		startSteps: startOfDaySteps,
		activeDocket,
		perStopSteps,
		endSteps: endOfDaySteps,
		runStarted,
	}), [activeDocket, endOfDaySteps, perStopSteps, runStarted, startOfDaySteps]);

	// The phase the driver is actually working: 'start' | 'stops' | 'end' | null.
	const activePhaseKey = useMemo(() => {
		if (!nextAction) {
			return null;
		}

		if (nextAction.type === 'start') {
			return 'start';
		}

		if (nextAction.type === 'end') {
			return 'end';
		}

		// startRun and stop both mean the driver is on the stops phase.
		return 'stops';
	}, [nextAction]);

	const visibleStartSteps = useMemo(() => startOfDaySteps.filter(step => step.status !== MY_DAY_STEP_STATUS.HIDDEN), [startOfDaySteps]);
	const visibleEndSteps = useMemo(() => endOfDaySteps.filter(step => step.status !== MY_DAY_STEP_STATUS.HIDDEN), [endOfDaySteps]);

	const getPhaseProgress = useCallback((phaseSteps) => {
		const countable = phaseSteps.filter(step => !step.trainingUnavailable && COUNTABLE_STEP_STATUSES.includes(step.status));
		return {
			doneCount: countable.filter(step => step.status === MY_DAY_STEP_STATUS.DONE).length,
			totalCount: countable.length,
		};
	}, []);
	const startOfDayProgress = useMemo(() => getPhaseProgress(startOfDaySteps), [getPhaseProgress, startOfDaySteps]);
	const endOfDayProgress = useMemo(() => getPhaseProgress(endOfDaySteps), [endOfDaySteps, getPhaseProgress]);
	const startOfDayComplete = startOfDayProgress.totalCount > 0 && startOfDayProgress.doneCount === startOfDayProgress.totalCount;
	const completedStopsCount = useMemo(() => (
		sortedDockets.filter(isDocketDone).length
	), [isDocketDone, sortedDockets]);
	const allStopsComplete = sortedDockets.length > 0 && completedStopsCount === sortedDockets.length;
	// Not done but marked "Couldn't complete": behind the driver, never counted as done.
	const couldNotCompleteDockets = useMemo(() => (
		sortedDockets.filter(docket => !isDocketDone(docket) && isDocketHandled(docket))
	), [isDocketDone, isDocketHandled, sortedDockets]);
	const allStopsHandled = sortedDockets.length > 0 && sortedDockets.every(isDocketHandled);

	const pickRows = useMemo(() => (
		sortedDockets
			.filter(docket => docket?.pickList?.id)
			.map(docket => ({
				docket,
				stopName: getDocketName(docket),
				picked: Boolean(docket.inventoryPicked),
				pickStatusLabel: getPickListStatusLabel({
					inventoryPicked: docket.inventoryPicked,
					inventoryPartiallyPicked: docket.inventoryPartiallyPicked,
					isAnyItemPicked: docket.pickList?.isAnyItemPicked,
					pendingLabel: 'Pick',
				}),
				incompletePick: Boolean(!docket.inventoryPicked && (
					docket.inventoryPartiallyPicked || docket.pickList?.isAnyItemPicked
				)),
			}))
	), [sortedDockets]);
	const accessKeyRows = useMemo(() => (
		buildAccessPreparationRows(sortedDockets).map(row => ({
			...row,
			stopName: getDocketName(row.docket),
		}))
	), [sortedDockets]);
	const ticketRows = useMemo(() => (
		sortedDockets
			.filter(docket => docket?.driverTicketCount > 0)
			.map(docket => ({
				docket,
				stopName: getDocketName(docket),
				ticketCount: docket.driverTicketCount,
				descriptions: (docket.ticketList?.driverItems || [])
					.map(item => item?.ticket?.description)
					.filter(Boolean),
			}))
	), [sortedDockets]);
	const moneyBagRows = useMemo(() => (
		sortedDockets
			.filter(docketRequiresMoneyBagReturn)
			.map(docket => ({
				id: docket.id,
				stopName: getDocketName(docket),
				cashBag: docket.cashBag,
			}))
	), [sortedDockets]);

	const stepByKey = useMemo(() => {
		const map = {};
		[...startOfDaySteps, ...endOfDaySteps].forEach((step) => {
			map[step.key] = step;
		});
		return map;
	}, [endOfDaySteps, startOfDaySteps]);

	// After an end-of-day clock-out "Not clocked in" would misread as a problem;
	// a mid-day clock-out (stops remaining) still needs a clock-in.
	const isClockedOutForDay = !clockStatus.isClockedIn && clockStatus.hasClockedOutToday && allStopsComplete;

	return {
		...data,
		serviceRun,
		isTraining,
		trainingContext,
		trainingOffline,
		isOnline,
		scopedQueue,
		trainingRouteParams,
		isLoading: data.loading || isLoadingPickSkips || isLoadingRestockSkips,
		canEditPickLists: hasPermission(ROLE_PERMISSIONS.ADD_EDIT_PICK_LISTS),
		canCreateTicket: hasPermission(ROLE_PERMISSIONS.ADD_EDIT_TICKETS),
		selectedDocketId,
		setSelectedDocketId,
		sortedDockets,
		hasEligiblePickListItems,
		nextIncompleteDocket,
		activeDocket,
		upcomingDocket,
		nowDocket,
		routeMapPoints,
		getDocketName,
		getDocketNavigationParams,
		reorderableStops,
		skipPickStepForDocket,
		skipRestockStepForDocket,
		buildStopSteps,
		startOfDaySteps,
		perStopSteps,
		endOfDaySteps,
		visibleStartSteps,
		visibleEndSteps,
		stepByKey,
		runStarted,
		nextAction,
		activePhaseKey,
		startOfDayProgress,
		endOfDayProgress,
		startOfDayComplete,
		completedStopsCount,
		allStopsComplete,
		couldNotCompleteDockets,
		allStopsHandled,
		isClockedOutForDay,
		pickRows,
		accessKeyRows,
		ticketRows,
		moneyBagRows,
	};
};

export default useDriverDayModel;
